import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '@/state/store/auth';
import { uploadDocxToStorage } from '@/modules/templates/services/docx-storage.service';
import type { FixedDocxDocument } from '../docx-review/document-payload';
import { createContractDocument } from '@/modules/org/contracts/services/contract-document.service';
import type {
  ContractSignatory,
  OrgContractDocumentInsertInput,
} from '@/modules/org/contracts/types/contract-document.types';
import type { ResolvedSignatory } from '../utils';
import {
  LEGAL_TEMPLATE_DOCUMENT_GENERATION_FEATURE_ID,
  updateSubscriptionUsage,
} from '@/modules/subscription/services/subscription.service';
import { useQueryClient } from '@tanstack/react-query';
import { activeSubscriptionQueryKey } from '@/modules/subscription/hooks/use-active-subscription';

export interface GenerationStep {
  key: 'merge' | 'save';
  title: string;
  description: string;
  progress: number;
}

/**
 * Generation only has to produce the merged .docx and persist it.
 *
 * It used to also convert to PDF and pre-detect signature locations. Both were
 * dropped: the review page renders the .docx (never that PDF), and the stored
 * locations were always discarded — send re-detects on a freshly converted PDF
 * because editing reflows the document. Removing them takes a server conversion
 * and a pdf.js parse off the critical path.
 */
export const DOCX_GENERATION_STEPS: GenerationStep[] = [
  {
    key: 'merge',
    title: 'Merging your details',
    description: 'Filling your information into the Word template.',
    progress: 40,
  },
  {
    key: 'save',
    title: 'Saving your document',
    description: 'Storing the contract in your workspace.',
    progress: 90,
  },
];

interface UseDocxGenerationOptions {
  /** Merges the filled values into the template and returns the .docx blob. Throws on error. */
  merge: () => Promise<Blob>;
  /** Raw filled values — persisted as FormData. */
  values: Record<string, string>;
  title: string;
  slug: string;
  templateId: string;
}

export interface UseDocxGenerationReturn {
  isGenerating: boolean;
  error: string | null;
  stepIndex: number;
  progress: number;
  steps: GenerationStep[];
  currentStep: GenerationStep;
  generate: (signatories: ResolvedSignatory[]) => Promise<void>;
  retry: () => void;
  reset: () => void;
}

export function useDocxGeneration({
  merge,
  values,
  title,
  slug,
  templateId,
}: UseDocxGenerationOptions): UseDocxGenerationReturn {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  // Keep the last signatory set so the loading-screen "Try again" can re-run.
  const lastSignatoriesRef = useRef<ResolvedSignatory[]>([]);

  const generate = useCallback(
    async (signatories: ResolvedSignatory[]) => {
      lastSignatoriesRef.current = signatories;
      setIsGenerating(true);
      setError(null);
      setStepIndex(0);
      setProgress(0);

      try {
        // 1. Merge → .docx blob
        setStepIndex(0);
        setProgress(DOCX_GENERATION_STEPS[0].progress);
        const docxBlob = await merge();

        // 2. Persist the merged .docx — the single source of truth for the
        // review page and for every PDF derived later. Fatal on failure: there
        // is no other stored artifact, so a contract without it is unopenable.
        setStepIndex(1);
        setProgress(DOCX_GENERATION_STEPS[1].progress);
        const fileBase = slug ? slug.replace(/[^a-zA-Z0-9_-]/g, '_') : 'document';
        const docxUpload = await uploadDocxToStorage(docxBlob, `${fileBase}.docx`);

        // 3. Persist the OrgContractDocument
        const { orgUDSId, sigOrgId, isAuthenticated, userId } = useAuthStore.getState();
        const now = new Date().toISOString();

        const contractSignatories: ContractSignatory[] = signatories.map((s) => ({
          id: crypto.randomUUID(),
          partyType: 'signatory',
          name: s.name,
          email: s.email,
          title: s.role || undefined,
          company: s.company || undefined,
          status: 'pending',
          updatedTime: now,
          order: s.signingOrder ?? null,
          // Resolved at send time against the freshly converted PDF — detecting
          // here would be discarded, since editing reflows the document.
          signatureLocation: null,
          // The stable link back to the template's signature variable, so an
          // anchor can be re-matched even when two signatories share its text.
          templateKey: s.key,
          signatureAnchor: s.signatureAnchor
            ? {
                paragraphIndex: s.signatureAnchor.paragraphIndex,
                originalText: s.signatureAnchor.originalText,
                contextBefore: s.signatureAnchor.contextBefore,
                contextAfter: s.signatureAnchor.contextAfter,
              }
            : null,
        }));

        const input: OrgContractDocumentInsertInput = {
          OrganizationId: orgUDSId ?? '',
          OrganizationIds: orgUDSId ? [orgUDSId] : undefined,
          ...(sigOrgId ? { SigOrgId: sigOrgId } : {}),
          Name: title,
          TemplateId: templateId,
          TemplateSlug: slug,
          TemplateTitle: title,
          Status: 'generation_completed',
          IsArchived: false,
          ProgressPercent: 100,
          FormData: JSON.stringify(values),
          Document: JSON.stringify({
            type: 'fixed_docx',
            docxUrl: docxUpload.url,
            docxFileId: docxUpload.fileId,
          } satisfies FixedDocxDocument),
          Signatories: JSON.stringify(contractSignatories),
        };

        const res = await createContractDocument(input);
        const itemId = res.insertOrgContractDocument.itemId;
        if (!itemId) {
          throw new Error('The document could not be saved. Please try again.');
        }

        if (isAuthenticated) {
          try {
            await updateSubscriptionUsage({
              featureId: LEGAL_TEMPLATE_DOCUMENT_GENERATION_FEATURE_ID,
              usage: 1,
            });
            void queryClient.invalidateQueries({
              queryKey: activeSubscriptionQueryKey(userId, sigOrgId),
            });
          } catch (usageError) {
            console.warn('Contract was generated, but subscription usage could not be updated:', usageError);
          }
        }

        setProgress(100);
        // Brief beat so the 100% completion registers before the route swap.
        await new Promise((resolve) => setTimeout(resolve, 400));
        navigate(`/templates/${slug}/review?document=${encodeURIComponent(itemId)}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Something went wrong while generating.');
        setIsGenerating(false);
      }
    },
    [merge, values, title, slug, templateId, navigate, queryClient]
  );

  const retry = useCallback(() => {
    void generate(lastSignatoriesRef.current);
  }, [generate]);

  const reset = useCallback(() => {
    setIsGenerating(false);
    setError(null);
    setStepIndex(0);
    setProgress(0);
  }, []);

  return {
    isGenerating,
    error,
    stepIndex,
    progress,
    steps: DOCX_GENERATION_STEPS,
    currentStep: DOCX_GENERATION_STEPS[Math.min(stepIndex, DOCX_GENERATION_STEPS.length - 1)],
    generate,
    retry,
    reset,
  };
}
